import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import type { IResourceDatabaseField } from '@/api/types';
import type { IDatabaseFieldColumnNames } from '@/components/common';
import type { ITableSortingState } from '@/components/common/table/types';

import {
  Table,
  DropdownMenu,
  ResourceTableCell,
  ResourceTableHeader,
  ResourceTableHeaderCell,
  ResourceTableRow,
} from '@/components/common';
import { CheckBlue, DatabaseFieldIconBlue } from '@/assets/icons';

interface IProps {
  items: IResourceDatabaseField[];
  sortingState: ITableSortingState<IDatabaseFieldColumnNames>;
  onSort: (columnName: IDatabaseFieldColumnNames) => void;
  className?: string;
}

const ResourceDatabaseFieldsTable = ({ items, sortingState, onSort, className }: IProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Table className={className}>
      <ResourceTableHeader>
        <ResourceTableHeaderCell width="26px" minWidth="26px" />
        <ResourceTableHeaderCell
          size={3}
          sortingEnabled
          sortingState={sortingState.name}
          onClick={() => onSort('name')}
        >{t`resources.foldersList.headerName`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.type}
          onClick={() => onSort('type')}
        >{t`resources.foldersList.headerType`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCellCentered
          size={1}
          sortingState={sortingState.searchable}
          onClick={() => onSort('searchable')}
        >{t`resources.foldersList.headerSearchable`}</ResourceTableHeaderCellCentered>
        <ResourceTableHeaderCellCentered
          size={1}
          sortingState={sortingState.required}
          onClick={() => onSort('required')}
        >{t`resources.foldersList.headerRequired`}</ResourceTableHeaderCellCentered>
        <ResourceTableHeaderCellCentered
          size={1}
          sortingState={sortingState.unique}
          onClick={() => onSort('unique')}
        >{t`resources.foldersList.headerUnique`}</ResourceTableHeaderCellCentered>
        <ResourceTableHeaderCell width="24px" minWidth="24px" className="p-0 ms-3" />
      </ResourceTableHeader>
      {items.map((item, id) => (
        <ResourceTableRow key={id}>
          <ResourceTableCell width="26px" minWidth="26px" className="m-0">
            <IconCell src={DatabaseFieldIconBlue} />
          </ResourceTableCell>
          <ResourceTableCell size={3}>{item.name}</ResourceTableCell>
          <ResourceTableCell size={2}>{item.type}</ResourceTableCell>
          <ResourceTableCell size={1}>{item.searchable && <IconCell src={CheckBlue} />}</ResourceTableCell>
          <ResourceTableCell size={1}>{item.required && <IconCell src={CheckBlue} />}</ResourceTableCell>
          <ResourceTableCell size={1}>{item.unique && <IconCell src={CheckBlue} />}</ResourceTableCell>
          <ResourceTableCell width="24px" minWidth="24px" className="dropdown-action">
            <DropdownMenu
              // TODO: Add option callbacks
              options={[
                {
                  label: t`common.btnEdit`,
                  callback: () => '',
                },
                {
                  label: t`common.btnDelete`,
                  callback: () => '',
                },
              ]}
            />
          </ResourceTableCell>
        </ResourceTableRow>
      ))}
    </Table>
  );
};

export { ResourceDatabaseFieldsTable };

const IconCell = styled.img`
  width: 100%;
  max-height: 16px;
`;

const ResourceTableHeaderCellCentered = styled(ResourceTableHeaderCell)`
  justify-content: center;
`;
